import type { ReactElement } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { EllipsisIcon, FolderPlusIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@/entities/user';

/** What can be done to one folder. */
export const FolderMenu = ({
  folderName,
  onRename,
  onNewSubfolder,
  onDelete,
}: {
  folderName: string;
  onRename: () => void;
  onNewSubfolder: () => void;
  onDelete: () => void;
}) => {
  const { t } = useTranslation('tools');
  const { canUploadBrandAssets, canManageBrandAssets } = usePermissions();

  /** A disabled item drops pointer events, so the span carries the hover that explains why. */
  const withReason = (allowed: boolean, item: ReactElement) =>
    allowed ? (
      item
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="block">{item}</span>
        </TooltipTrigger>
        <TooltipContent side="right">
          {t('brandMediaLibrary.permissions.noPermission')}
        </TooltipContent>
      </Tooltip>
    );

  return (
    <DropdownMenu>
      {/* Both triggers hand their props to the same button, and both set `data-state`. In an
          `asChild` chain the inner one wins, so the menu's trigger has to be the outer of the two:
          the button stays visible on `data-[state=open]` while its menu is open, and reading the
          tooltip's state there would fade it out as soon as the pointer left. `Tooltip` itself
          renders no element, so wrapping it around the menu trigger changes nothing structurally. */}
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              // The negative margin keeps a 24px target from setting the row's height: without it a
              // folder row stands taller than the rows that carry no menu, in the same list.
              className="-my-0.5 size-6 shrink-0 opacity-0 transition-opacity duration-fast ease-out group-hover:opacity-100 data-[state=open]:opacity-100"
              aria-label={t('brandMedia.folderActions', { name: folderName })}
              // The row underneath is a filter; opening its menu is not choosing it.
              onClick={(event) => event.stopPropagation()}
            >
              <EllipsisIcon className="size-4" />
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>{t('brandMedia.folderActions', { name: folderName })}</TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="start"
        // The menu renders inside the folder row, which is itself a click target, so a click on an
        // item bubbles out and changes which folder the library filters on. Harmless for a rename;
        // for a delete it moves the filter onto the folder that is about to stop existing.
        onClick={(event) => event.stopPropagation()}
      >
        {withReason(
          canUploadBrandAssets,
          <DropdownMenuItem disabled={!canUploadBrandAssets} onClick={onNewSubfolder}>
            <FolderPlusIcon className="size-4" />
            {t('brandMedia.newSubfolder')}
          </DropdownMenuItem>,
        )}

        {withReason(
          canManageBrandAssets,
          <DropdownMenuItem disabled={!canManageBrandAssets} onClick={onRename}>
            <PencilIcon className="size-4" />
            {t('brandMedia.rename')}
          </DropdownMenuItem>,
        )}

        {withReason(
          canManageBrandAssets,
          <DropdownMenuItem
            variant="destructive"
            disabled={!canManageBrandAssets}
            onClick={onDelete}
          >
            <Trash2Icon className="size-4" />
            {t('brandMedia.deleteFolder')}
          </DropdownMenuItem>,
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
